import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  X, 
  Wrench, 
  Calendar, 
  MapPin, 
  User, 
  Phone, 
  IndianRupee, 
  Clock, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  ChevronLeft,
  Star,
  Search,
  Building2,
  Users,
  Info,
  Check,
  CreditCard,
  ArrowUpRight,
  Navigation,
  Compass,
  Lock
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { ConsumerAuthModal } from '../consumer/ConsumerAuthModal';

export interface PublicServiceProvider {
  tenant_id: string;
  branch_id: string;
  business_name: string;
  branch_name?: string | null;
  city?: string | null;
  logo_url?: string | null;
  distance_km?: number | null;
  avg_rating?: number | null;
  ratings_count?: number | null;
  services_count?: number | null;
  min_price?: number | null;
  professionals_count?: number | null;
}

export interface CatalogServiceItem {
  catalog_item_id: string;
  service_name: string;
  base_price: number;
  unit_name?: string | null;
  duration_minutes?: number | null;
  price_on_visit?: boolean | null;
  photo?: string | null;
  description?: string | null;
  gst_rate?: number | null;
}

export interface CatalogCategory {
  category_id: string;
  category_name: string;
  items: CatalogServiceItem[];
}

export interface PublicServiceCatalogData {
  business_name: string;
  logo_url?: string | null;
  avg_rating?: number | null;
  ratings_count?: number | null;
  branch?: {
    address?: string | null;
    city?: string | null;
    pincode?: string | null;
    branch_name?: string | null;
  } | null;
  booking_charge?: {
    enabled?: boolean;
    applies_online?: boolean;
    charge_type?: string | null;
    charge_amount?: number | null;
    charge_percentage?: number | null;
    type?: string | null;
    amount?: number | null;
    rate?: number | null;
  } | null;
  categories: CatalogCategory[];
}

interface PublicServiceBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId?: string | null;
  branchId?: string | null;
  initialAddress?: string | null;
  initialLat?: number | null;
  initialLng?: number | null;
  preselectedServiceId?: string | null;
  onBookingSuccess?: (bookingId?: string) => void;
}

export function PublicServiceBookingModal({
  isOpen,
  onClose,
  tenantId,
  branchId,
  initialAddress,
  initialLat,
  initialLng,
  preselectedServiceId,
  onBookingSuccess
}: PublicServiceBookingModalProps) {
  const { user } = useAuth();
  const supabase = getSupabaseClient();

  // Navigation step in marketplace
  const [step, setStep] = useState<'providers' | 'catalog' | 'book' | 'success'>('providers');

  // Pincode & Search state
  const extractPincode = (addr?: string | null) => {
    if (!addr) return '271801';
    const match = addr.match(/\b\d{6}\b/);
    return match ? match[0] : '271801';
  };

  const [pincode, setPincode] = useState<string>(() => extractPincode(initialAddress));
  const [pincodeInput, setPincodeInput] = useState<string>(() => extractPincode(initialAddress));
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Area & Providers state
  const [checkingArea, setCheckingArea] = useState<boolean>(false);
  const [areaStatus, setAreaStatus] = useState<{
    checked: boolean;
    available: boolean;
    reason: string | null;
  }>({ checked: false, available: true, reason: null });

  const [loadingProviders, setLoadingProviders] = useState<boolean>(false);
  const [providers, setProviders] = useState<PublicServiceProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<PublicServiceProvider | null>(null);

  // Catalog state
  const [loadingCatalog, setLoadingCatalog] = useState<boolean>(false);
  const [catalog, setCatalog] = useState<PublicServiceCatalogData | null>(null);
  const [selectedItem, setSelectedItem] = useState<CatalogServiceItem | null>(null);

  // Booking Form State
  const [quantity, setQuantity] = useState<number>(1);
  const [notes, setNotes] = useState<string>('');
  const [customerName, setCustomerName] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [customerAddress, setCustomerAddress] = useState<string>(initialAddress || '');
  const [bookingMode, setBookingMode] = useState<'on_demand' | 'scheduled'>('on_demand');
  const [scheduledAt, setScheduledAt] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Auth Gate state
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);

  // Location Gate State
  const [detectedLat, setDetectedLat] = useState<number | null>(null);
  const [detectedLng, setDetectedLng] = useState<number | null>(null);
  const [locationAccuracy, setLocationAccuracy] = useState<number | null>(null);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'detecting' | 'detected' | 'denied' | 'error'>('idle');
  const [locationErrorMessage, setLocationErrorMessage] = useState<string>('');

  // Geolocation detector (high accuracy, 10s timeout)
  const requestUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('error');
      setLocationErrorMessage('Geolocation is not supported by your browser');
      return;
    }

    setLocationStatus('detecting');
    setLocationErrorMessage('');

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const acc = Math.round(pos.coords.accuracy);

        // India bounds check: lat 6-38, lng 68-98
        if (lat < 6 || lat > 38 || lng < 68 || lng > 98) {
          setLocationStatus('error');
          setLocationErrorMessage('Location detected is outside India (lat 6-38, lng 68-98).');
          return;
        }

        setDetectedLat(lat);
        setDetectedLng(lng);
        setLocationAccuracy(acc);
        setLocationStatus('detected');
      },
      (err) => {
        console.warn('Geolocation error:', err);
        if (err.code === err.PERMISSION_DENIED) {
          setLocationStatus('denied');
          setLocationErrorMessage('Location permission was denied. Please allow location access in your browser settings.');
        } else if (err.code === err.TIMEOUT) {
          setLocationStatus('error');
          setLocationErrorMessage('Location request timed out. Please check GPS and try again.');
        } else {
          setLocationStatus('error');
          setLocationErrorMessage(err.message || 'Unable to retrieve location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Check permission state immediately on entering checkout
  useEffect(() => {
    if (step === 'book') {
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'geolocation' as PermissionName })
          .then((permissionStatus) => {
            if (permissionStatus.state === 'denied') {
              setLocationStatus('denied');
              setLocationErrorMessage('Location permission was denied. Please allow location access in your browser or device settings.');
            } else {
              requestUserLocation();
            }
          })
          .catch(() => {
            requestUserLocation();
          });
      } else {
        requestUserLocation();
      }
    }
  }, [step]);

  // Success Confirmation State
  const [successInfo, setSuccessInfo] = useState<{
    bookingId?: string | null;
    bookingNumber: string;
    status: string;
    bookingChargeDue: number;
    bookingChargeNote: string;
  } | null>(null);
  const [onlinePayLoading, setOnlinePayLoading] = useState<boolean>(false);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState<boolean>(false);

  // Pre-fill customer details from Auth
  useEffect(() => {
    if (user) {
      if (user.user_metadata?.full_name) {
        setCustomerName(user.user_metadata.full_name);
      }
      if (user.phone) {
        setCustomerPhone(user.phone);
      }
    }
  }, [user]);

  // Keep address updated if prop changes
  useEffect(() => {
    if (initialAddress && !customerAddress) {
      setCustomerAddress(initialAddress);
      const pin = extractPincode(initialAddress);
      setPincode(pin);
      setPincodeInput(pin);
    }
  }, [initialAddress]);

  // Check pincode and fetch providers
  const checkAreaAndLoadProviders = async (pinToCheck: string, search?: string) => {
    if (!supabase) return;
    const cleanPin = pinToCheck.replace(/\D/g, '').slice(0, 6);
    if (!cleanPin || cleanPin.length !== 6) {
      setAreaStatus({ checked: true, available: false, reason: 'invalid_pincode' });
      setProviders([]);
      return;
    }

    setCheckingArea(true);
    setLoadingProviders(true);
    try {
      // 1. Pincode check via public_services_check_pincode RPC
      const { data: checkData, error: checkErr } = await supabase.rpc('public_services_check_pincode', {
        p_pincode: cleanPin,
      });

      if (checkErr) throw checkErr;

      const isAvail = Boolean(checkData?.available);
      const resReason = checkData?.reason || null;
      setAreaStatus({
        checked: true,
        available: isAvail,
        reason: resReason,
      });

      if (!isAvail) {
        setProviders([]);
        return;
      }

      // 2. Load provider list via get_public_service_providers RPC
      const { data: provData, error: provErr } = await supabase.rpc('get_public_service_providers', {
        p_pincode: cleanPin,
        p_lat: initialLat || null,
        p_lng: initialLng || null,
        p_search: (search ?? searchQuery).trim() || null,
      });

      if (provErr) throw provErr;

      const list = (provData as PublicServiceProvider[]) || [];
      // Requirement 5: Console-log the exact RPC response when the list is empty
      if (!list || list.length === 0) {
        console.log('get_public_service_providers empty response:', provData);
      }
      setProviders(list);
    } catch (err: any) {
      console.error('Error checking area or fetching providers:', err);
      // Real Postgres error displayed in toast
      toast.error(err.message || 'Failed to check area availability');
    } finally {
      setCheckingArea(false);
      setLoadingProviders(false);
    }
  };

  // Initial load when modal opens
  useEffect(() => {
    if (isOpen) {
      checkAreaAndLoadProviders(pincode);
    }
  }, [isOpen]);

  // Handler for changing pincode
  const handlePincodeSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const clean = pincodeInput.replace(/\D/g, '').slice(0, 6);
    setPincode(clean);
    checkAreaAndLoadProviders(clean);
  };

  // Handler for selecting provider -> loads catalog
  const handleSelectProvider = async (prov: PublicServiceProvider) => {
    if (!supabase) return;
    setSelectedProvider(prov);
    setLoadingCatalog(true);
    setStep('catalog');

    try {
      const { data: catData, error: catErr } = await supabase.rpc('get_public_service_catalog', {
        p_tenant_id: prov.tenant_id,
        p_branch_id: prov.branch_id,
      });

      if (catErr) throw catErr;

      setCatalog(catData as PublicServiceCatalogData);
    } catch (err: any) {
      console.error('Error loading service catalog:', err);
      toast.error(err.message || 'Failed to load services for this provider');
    } finally {
      setLoadingCatalog(false);
    }
  };

  // Handler for selecting service -> opens book form
  const handleSelectService = (item: CatalogServiceItem) => {
    setSelectedItem(item);
    setQuantity(1);
    setNotes('');

    // Task 1: Login gate - when a guest taps Book / Continue, show small sheet "Login to book"
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    setStep('book');
  };

  // Submit Booking via book_public_service RPC
  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !selectedProvider || !selectedItem) return;

    if (!user) {
      setShowLoginModal(true);
      return;
    }

    // Task 2: Location gate at checkout
    if (!detectedLat || !detectedLng || locationStatus !== 'detected') {
      toast.error('Please turn on location and allow access so the professional can find you.');
      return;
    }

    if (!customerName.trim()) {
      toast.error('Please enter customer name');
      return;
    }
    if (!customerPhone.trim()) {
      toast.error('Please enter customer phone number');
      return;
    }
    if (!customerAddress.trim()) {
      toast.error('Please enter complete service address');
      return;
    }
    if (!pincode.trim() || pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit delivery pincode');
      return;
    }

    setSubmitting(true);
    try {
      const scheduledIso = bookingMode === 'scheduled' && scheduledAt
        ? new Date(scheduledAt).toISOString()
        : null;

      const { data, error } = await supabase.rpc('book_public_service', {
        p_tenant_id: selectedProvider.tenant_id,
        p_branch_id: selectedProvider.branch_id,
        p_catalog_item_id: selectedItem.catalog_item_id,
        p_customer_name: customerName.trim(),
        p_customer_phone: customerPhone.trim(),
        p_customer_address: customerAddress.trim(),
        p_pincode: pincode.trim(),
        p_booking_mode: bookingMode,
        p_scheduled_at: scheduledIso,
        p_quantity: Math.max(1, quantity),
        p_notes: notes.trim() || null,
        p_lat: detectedLat,
        p_lng: detectedLng,
      });

      if (error) throw error;

      const bNumber = data?.booking_number || data?.id || (typeof data === 'string' ? data : `BK-${Date.now().toString().slice(-6)}`);
      const bStatus = data?.status || 'Requested';
      const chargeDue = Number(data?.booking_charge_due || data?.booking_charge || 0);
      const chargeNote = data?.booking_charge_note || (chargeDue > 0 ? `Minimum booking charge ₹${chargeDue} applicable.` : '');

      let actualBookingId = data?.id || data?.booking_id || (typeof data === 'string' && data.includes('-') ? data : null);
      if (!actualBookingId && bNumber && supabase) {
        try {
          const { data: bRow } = await supabase
            .from('service_bookings')
            .select('id, booking_number, booking_charge_due, advance_amount')
            .eq('booking_number', bNumber)
            .maybeSingle();
          if (bRow) {
            actualBookingId = bRow.id;
          }
        } catch (fetchErr) {
          console.warn('Could not fetch booking row for UUID:', fetchErr);
        }
      }

      setPaymentLinkUrl(null);
      setIsPaymentConfirmed(false);
      setSuccessInfo({
        bookingId: actualBookingId,
        bookingNumber: bNumber,
        status: bStatus,
        bookingChargeDue: chargeDue,
        bookingChargeNote: chargeNote,
      });
      setStep('success');
      toast.success('Service booked successfully! A professional will be assigned.');
      if (onBookingSuccess) {
        onBookingSuccess(bNumber);
      }
    } catch (err: any) {
      console.error('Service booking error:', err);
      // Real Postgres error message displayed in toast (do not swallow)
      toast.error(err.message || 'Failed to book service');
    } finally {
      setSubmitting(false);
    }
  };

  // Task 3: Invoke create-booking-charge-link for online payment
  const handlePayNow = async () => {
    if (!successInfo?.bookingId || !supabase) {
      toast.error('Booking ID not found');
      return;
    }
    setOnlinePayLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-booking-charge-link', {
        body: { booking_id: successInfo.bookingId }
      });

      if (error) {
        let errorMsg = error.message || 'Failed to create payment link';
        try {
          const errBody = await (error as any).context?.json?.();
          if (errBody?.error) errorMsg = errBody.error;
          else if (errBody?.message) errorMsg = errBody.message;
        } catch {}
        toast.error(errorMsg);
        return;
      }

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      const shortUrl = data.short_url;
      setPaymentLinkUrl(shortUrl);
      toast.success(data?.reused ? 'Reused active payment link' : 'Payment link generated!');

      if (shortUrl) {
        window.open(shortUrl, '_blank');
      }
    } catch (err: any) {
      console.error('Pay now exception:', err);
      toast.error(err.message || 'Error generating payment link');
    } finally {
      setOnlinePayLoading(false);
    }
  };

  // Task 3: Poll booking status every 5s up to 2 minutes after payment link is open
  useEffect(() => {
    if (!paymentLinkUrl || !successInfo?.bookingId || isPaymentConfirmed || !supabase) return;

    let pollCount = 0;
    const maxPolls = 24; // 24 * 5s = 120s (2 minutes)

    const interval = setInterval(async () => {
      pollCount++;
      try {
        const { data: bRow } = await supabase
          .from('service_bookings')
          .select('id, advance_amount, booking_charge_due, status')
          .eq('id', successInfo.bookingId)
          .maybeSingle();

        if (bRow) {
          const due = Number(bRow.booking_charge_due || 0);
          const adv = Number(bRow.advance_amount || 0);
          if (adv >= due && due > 0) {
            setIsPaymentConfirmed(true);
            toast.success('Payment received! Booking confirmed.');
            clearInterval(interval);
          }
        }
      } catch (pollErr) {
        console.error('Customer poll error:', pollErr);
      }

      if (pollCount >= maxPolls) {
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [paymentLinkUrl, successInfo?.bookingId, isPaymentConfirmed, supabase]);

  if (!isOpen) return null;

  // Unserviceable message helper
  const getUnavailableMessage = (reason: string | null) => {
    if (reason === 'not_serviceable') {
      return 'Home services are not live in your area yet';
    }
    if (reason === 'no_providers') {
      return 'No service provider is accepting bookings in your area right now';
    }
    if (reason === 'invalid_pincode') {
      return 'Enter a valid 6-digit pincode';
    }
    return 'Home services are not live in your area yet';
  };

  // Booking charge banner text helper
  const getBookingChargeBannerText = () => {
    if (!catalog?.booking_charge?.enabled) return null;
    const bc = catalog.booking_charge;
    if (bc.applies_online === false) return null;

    const isPercent = bc.type === 'percentage' || bc.type === 'percent' || Boolean(bc.charge_percentage);
    if (isPercent) {
      const rate = bc.charge_percentage || bc.rate || 10;
      return `Minimum booking amount ${rate}% of your booking total - adjusted in your final bill`;
    }
    const amt = bc.charge_amount || bc.amount || 50;
    return `Minimum booking amount ₹${amt} per booking - adjusted in your final bill`;
  };

  const bookingChargeBannerText = getBookingChargeBannerText();

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
        
        {/* ======================================================== */}
        {/* Top Header Bar                                           */}
        {/* ======================================================== */}
        <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 shrink-0 bg-slate-50/50 dark:bg-slate-900">
          <div className="flex items-center gap-2.5 min-w-0">
            {step !== 'providers' && step !== 'success' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (step === 'book') setStep('catalog');
                  else if (step === 'catalog') setStep('providers');
                }}
                className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900 dark:hover:text-white"
              >
                <ChevronLeft size={20} />
              </Button>
            )}

            <div className="w-9 h-9 rounded-xl bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 flex items-center justify-center shrink-0">
              <Wrench size={18} />
            </div>

            <div className="min-w-0">
              <h3 className="text-base font-bold text-slate-900 dark:text-white truncate">
                {step === 'providers' && 'Home Services Marketplace'}
                {step === 'catalog' && (catalog?.business_name || 'Service Catalog')}
                {step === 'book' && `Book ${selectedItem?.service_name || 'Service'}`}
                {step === 'success' && 'Booking Requested'}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {step === 'providers' && `Serving Pincode ${pincode}`}
                {step === 'catalog' && (catalog?.branch?.city ? `${catalog.branch.branch_name || 'Branch'} • ${catalog.branch.city}` : 'Choose services')}
                {step === 'book' && selectedProvider?.business_name}
                {step === 'success' && 'Your appointment has been logged'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* ======================================================== */}
        {/* STEP 1: Providers List & Area Availability Check         */}
        {/* ======================================================== */}
        {step === 'providers' && (
          <div className="overflow-y-auto p-4 sm:p-6 space-y-4 flex-1">
            {/* Pincode & Search Bar */}
            <div className="bg-slate-50 dark:bg-slate-800/40 p-3 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2.5">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                <form onSubmit={handlePincodeSubmit} className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 shrink-0">
                    <MapPin size={14} className="text-teal-600 dark:text-teal-400" />
                    <span>Pincode:</span>
                  </div>
                  <Input
                    type="text"
                    maxLength={6}
                    value={pincodeInput}
                    onChange={e => setPincodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="e.g. 271801"
                    className="h-8 w-28 text-xs font-mono font-bold bg-white dark:bg-slate-800"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={checkingArea}
                    className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-700 dark:hover:bg-slate-600 font-semibold"
                  >
                    {checkingArea ? <Loader2 size={12} className="animate-spin" /> : 'Check'}
                  </Button>
                </form>

                {/* Optional Search */}
                <div className="relative sm:w-56">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search provider..."
                    value={searchQuery}
                    onChange={e => {
                      setSearchQuery(e.target.value);
                      checkAreaAndLoadProviders(pincode, e.target.value);
                    }}
                    className="h-8 pl-7 text-xs bg-white dark:bg-slate-800"
                  />
                </div>
              </div>
            </div>

            {/* If checking area */}
            {checkingArea || loadingProviders ? (
              <div className="py-16 text-center space-y-2">
                <Loader2 size={28} className="animate-spin mx-auto text-teal-600" />
                <p className="text-xs text-slate-500 font-medium">Checking area availability & service providers...</p>
              </div>
            ) : areaStatus.checked && !areaStatus.available ? (
              /* Requirement 1: Unavailable by reason - NEVER show the old "Unavailable services" list */
              <div className="py-12 px-6 text-center bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl space-y-3">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto">
                  <AlertCircle size={24} />
                </div>
                <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                  {getUnavailableMessage(areaStatus.reason)}
                </h4>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Please check with a different pincode or try again later.
                </p>
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPincodeInput('271801');
                      setPincode('271801');
                      checkAreaAndLoadProviders('271801');
                    }}
                    className="text-xs border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300"
                  >
                    Try Default Pincode 271801
                  </Button>
                </div>
              </div>
            ) : providers.length === 0 ? (
              <div className="py-12 px-4 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl space-y-2">
                <Building2 size={28} className="mx-auto text-slate-300 dark:text-slate-600" />
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  No service providers found in pincode {pincode}
                </p>
                <p className="text-xs text-slate-400">
                  Try adjusting your search or verifying your address location.
                </p>
              </div>
            ) : (
              /* Requirement 2: Provider List Cards */
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Available Service Providers ({providers.length})
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {providers.map(prov => (
                    <div
                      key={`${prov.tenant_id}-${prov.branch_id}`}
                      onClick={() => handleSelectProvider(prov)}
                      className="group bg-white dark:bg-slate-850 p-4 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-teal-500/50 hover:shadow-md transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3.5"
                    >
                      <div className="flex items-center gap-3.5 min-w-0">
                        {/* Logo / Fallback Initial */}
                        {prov.logo_url ? (
                          <img
                            src={prov.logo_url}
                            alt={prov.business_name}
                            className="w-12 h-12 rounded-xl object-cover border border-slate-200 dark:border-slate-700 shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 font-bold text-lg flex items-center justify-center border border-teal-200 dark:border-teal-800 shrink-0">
                            {prov.business_name?.charAt(0)?.toUpperCase() || 'S'}
                          </div>
                        )}

                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors truncate">
                              {prov.business_name}
                            </h4>
                            {/* Rating badge */}
                            {prov.avg_rating && (prov.ratings_count ?? 0) > 0 ? (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                                <Star size={10} className="fill-amber-400" />
                                <span>{prov.avg_rating} ({prov.ratings_count})</span>
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                New
                              </span>
                            )}
                          </div>

                          <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-0.5">
                            <span>{prov.branch_name || 'Main Branch'}</span>
                            {prov.city && <span>• {prov.city}</span>}
                            {prov.distance_km != null && (
                              <span className="text-slate-400">({prov.distance_km} km)</span>
                            )}
                          </p>

                          <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                            <span>{prov.services_count ?? 0} services</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Users size={11} className="text-slate-400" />
                              <span>{prov.professionals_count ?? 0} professionals</span>
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-100 dark:border-slate-800 shrink-0">
                        <div className="text-left sm:text-right">
                          <span className="text-[10px] uppercase text-slate-400 block font-semibold">Starting from</span>
                          <span className="text-sm font-bold text-slate-900 dark:text-white font-mono">
                            ₹{prov.min_price ?? 0}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white font-semibold px-3"
                        >
                          View Services
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ======================================================== */}
        {/* STEP 2: Provider Page / Catalog View                     */}
        {/* ======================================================== */}
        {step === 'catalog' && (
          <div className="overflow-y-auto p-4 sm:p-6 space-y-5 flex-1">
            {loadingCatalog ? (
              <div className="py-20 text-center space-y-2">
                <Loader2 size={28} className="animate-spin mx-auto text-teal-600" />
                <p className="text-xs text-slate-500 font-medium">Loading service catalog...</p>
              </div>
            ) : !catalog ? (
              <div className="py-12 text-center text-slate-400">
                Failed to load service catalog for this provider.
              </div>
            ) : (
              <>
                {/* Header (Logo, Name, Address/City, Rating) */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-800 flex items-start gap-4">
                  {catalog.logo_url ? (
                    <img
                      src={catalog.logo_url}
                      alt={catalog.business_name}
                      className="w-14 h-14 rounded-2xl object-cover border border-slate-200 dark:border-slate-700 shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-2xl bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 font-bold text-xl flex items-center justify-center border border-teal-200 dark:border-teal-800 shrink-0">
                      {catalog.business_name?.charAt(0)?.toUpperCase() || 'S'}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">
                        {catalog.business_name}
                      </h3>
                      {catalog.avg_rating && (catalog.ratings_count ?? 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                          <Star size={11} className="fill-amber-400" />
                          <span>{catalog.avg_rating} ({catalog.ratings_count})</span>
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                          New
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-1">
                      <MapPin size={12} className="text-slate-400 shrink-0" />
                      <span className="truncate">
                        {catalog.branch?.address ? `${catalog.branch.address} ` : ''}
                        {catalog.branch?.city || 'Bahraich'}
                        {catalog.branch?.pincode ? ` - ${catalog.branch.pincode}` : ''}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Requirement 3: ONE Booking Charge Banner on Page */}
                {bookingChargeBannerText && (
                  <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl flex items-center gap-2.5 text-xs text-indigo-900 dark:text-indigo-200">
                    <Info size={16} className="text-indigo-600 dark:text-indigo-400 shrink-0" />
                    <span className="font-medium">{bookingChargeBannerText}</span>
                  </div>
                )}

                {/* Categories as Sections */}
                {catalog.categories && catalog.categories.length > 0 ? (
                  <div className="space-y-6">
                    {catalog.categories.map(cat => (
                      <div key={cat.category_id} className="space-y-3">
                        <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100 dark:border-slate-800">
                          <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                          <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                            {cat.category_name}
                          </h4>
                          <span className="text-[11px] text-slate-400">({cat.items.length})</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {cat.items.map(item => (
                            <div
                              key={item.catalog_item_id}
                              className="bg-white dark:bg-slate-850 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between gap-3 hover:border-slate-300 dark:hover:border-slate-700 transition-all"
                            >
                              <div className="flex items-start gap-3">
                                {item.photo ? (
                                  <img
                                    src={item.photo}
                                    alt={item.service_name}
                                    className="w-14 h-14 rounded-lg object-cover border border-slate-200 dark:border-slate-700 shrink-0"
                                  />
                                ) : (
                                  <div className="w-14 h-14 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center shrink-0">
                                    <Wrench size={18} />
                                  </div>
                                )}

                                <div className="min-w-0 flex-1">
                                  <h5 className="text-xs font-bold text-slate-900 dark:text-white">
                                    {item.service_name}
                                  </h5>
                                  {item.description && (
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">
                                      {item.description}
                                    </p>
                                  )}
                                  {item.duration_minutes ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 mt-1">
                                      <Clock size={10} />
                                      <span>{item.duration_minutes} mins</span>
                                    </span>
                                  ) : null}
                                </div>
                              </div>

                              <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                                <div>
                                  <span className="text-xs font-bold text-slate-900 dark:text-white font-mono">
                                    {item.price_on_visit ? (
                                      'Price on visit'
                                    ) : (
                                      `₹${item.base_price}${item.unit_name ? ` / ${item.unit_name}` : ''}`
                                    )}
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => handleSelectService(item)}
                                  className="h-7 text-xs bg-teal-600 hover:bg-teal-700 text-white font-semibold px-3"
                                >
                                  Book Service
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-slate-400">
                    No services currently listed for this provider.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ======================================================== */}
        {/* STEP 3: Booking Form (Customer Info, Timing, Notes)      */}
        {/* ======================================================== */}
        {step === 'book' && selectedItem && (
          <form onSubmit={handleSubmitBooking} className="overflow-y-auto p-4 sm:p-6 space-y-4 flex-1 text-xs">
            {/* Selected Service Card */}
            <div className="p-3.5 rounded-xl bg-teal-50/60 dark:bg-teal-950/20 border border-teal-200 dark:border-teal-800 flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold text-teal-700 dark:text-teal-400 block">
                  Booking Selected Service
                </span>
                <span className="text-sm font-bold text-slate-900 dark:text-white block mt-0.5">
                  {selectedItem.service_name}
                </span>
                <span className="text-xs font-mono font-semibold text-slate-600 dark:text-slate-300">
                  {selectedItem.price_on_visit ? 'Price on visit' : `₹${selectedItem.base_price}${selectedItem.unit_name ? ` / ${selectedItem.unit_name}` : ''}`}
                </span>
              </div>
              <div className="text-right text-[11px] text-slate-500">
                <span className="block font-semibold text-slate-700 dark:text-slate-300">{selectedProvider?.business_name}</span>
                <span>Pincode {pincode}</span>
              </div>
            </div>

            {/* Task 2: Location Status Row & Blocking Gate Card */}
            <div className="space-y-2.5">
              {/* Location Status Bar */}
              <div className={`p-2.5 rounded-xl border flex items-center justify-between gap-2 text-xs transition-colors ${
                locationStatus === 'detected'
                  ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200'
                  : locationStatus === 'detecting'
                  ? 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-800 text-sky-900 dark:text-sky-200'
                  : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800 text-rose-900 dark:text-rose-200'
              }`}>
                <div className="flex items-center gap-2">
                  {locationStatus === 'detecting' ? (
                    <Loader2 size={15} className="animate-spin text-sky-600 dark:text-sky-400 shrink-0" />
                  ) : locationStatus === 'detected' ? (
                    <CheckCircle2 size={15} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                  ) : (
                    <AlertCircle size={15} className="text-rose-600 dark:text-rose-400 shrink-0" />
                  )}
                  <div>
                    <span className="font-semibold">
                      Location:{' '}
                      {locationStatus === 'detected' && 'detected'}
                      {locationStatus === 'detecting' && 'Detecting GPS...'}
                      {locationStatus !== 'detected' && locationStatus !== 'detecting' && 'OFF / Not allowed'}
                    </span>
                    {locationStatus === 'detected' && locationAccuracy && (
                      <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-normal ml-1.5">
                        (±{locationAccuracy}m accuracy • lat {detectedLat?.toFixed(4)}, lng {detectedLng?.toFixed(4)})
                      </span>
                    )}
                  </div>
                </div>

                {locationStatus !== 'detected' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={requestUserLocation}
                    disabled={locationStatus === 'detecting'}
                    className="h-7 text-xs px-2.5 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 shrink-0"
                  >
                    {locationStatus === 'detecting' ? (
                      <Loader2 size={12} className="animate-spin mr-1" />
                    ) : (
                      <Navigation size={12} className="mr-1" />
                    )}
                    Try again
                  </Button>
                )}
              </div>

              {/* Task 2 & 5: Blocking Card if Permission Denied or Position Fails */}
              {locationStatus !== 'detected' && (
                <div className="p-4 rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50/70 dark:bg-rose-950/30 text-rose-950 dark:text-rose-200 space-y-3">
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0 mt-0.5">
                      <MapPin size={18} />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-rose-900 dark:text-rose-200">
                        Please turn on location to proceed
                      </h4>
                      <p className="text-[11px] text-rose-700 dark:text-rose-300 mt-0.5 leading-relaxed">
                        The professional needs your real GPS coordinates to reach your home for service.
                      </p>
                    </div>
                  </div>

                  <div className="bg-white/80 dark:bg-slate-900/80 p-3 rounded-lg border border-rose-200 dark:border-rose-900/40 text-[11px] text-slate-700 dark:text-slate-300 space-y-1.5">
                    <div className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                      Quick Steps to enable:
                    </div>
                    <div className="flex items-start gap-1.5">
                      <span className="font-bold text-rose-600">1.</span>
                      <span>Turn ON Device / Phone Location (GPS) in your phone settings or notification panel.</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <span className="font-bold text-rose-600">2.</span>
                      <span>In your browser, tap the lock/tune icon near the URL address bar and set <strong>Location</strong> to <strong>Allow</strong>.</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <span className="font-bold text-rose-600">3.</span>
                      <span>Tap the <strong>Try again</strong> button below to detect coordinates.</span>
                    </div>
                  </div>

                  {locationErrorMessage && (
                    <div className="text-[11px] text-rose-600 dark:text-rose-400 font-mono">
                      Error: {locationErrorMessage}
                    </div>
                  )}

                  <div className="pt-1 flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={requestUserLocation}
                      disabled={locationStatus === 'detecting'}
                      className="h-8 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white gap-1.5 shadow-xs"
                    >
                      {locationStatus === 'detecting' ? (
                        <>
                          <Loader2 size={13} className="animate-spin" />
                          <span>Detecting...</span>
                        </>
                      ) : (
                        <>
                          <Navigation size={13} />
                          <span>Try again</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Quantity */}
            <div>
              <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Quantity / Number of Units <span className="text-rose-500">*</span>
              </label>
              <Input
                type="number"
                min="1"
                step="1"
                value={quantity}
                onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                required
                className="h-10 font-bold w-36"
              />
            </div>

            {/* Customer Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Your Full Name <span className="text-rose-500">*</span>
                </label>
                <Input
                  placeholder="e.g. Rahul Sharma"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  required
                  className="h-10"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Contact Phone <span className="text-rose-500">*</span>
                </label>
                <Input
                  placeholder="10-digit mobile number"
                  value={customerPhone}
                  onChange={e => setCustomerPhone(e.target.value)}
                  required
                  className="h-10"
                />
              </div>
            </div>

            {/* Address */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Complete Service Address <span className="text-rose-500">*</span>
                </label>
                <Input
                  placeholder="House / Flat No, Street, Landmark..."
                  value={customerAddress}
                  onChange={e => setCustomerAddress(e.target.value)}
                  required
                  className="h-10"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Delivery Pincode
                </label>
                <Input
                  value={pincode}
                  readOnly
                  className="h-10 bg-slate-100 dark:bg-slate-800 font-mono font-bold cursor-not-allowed"
                />
              </div>
            </div>

            {/* Booking Timing */}
            <div>
              <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">
                Appointment Timing
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label
                  className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                    bookingMode === 'on_demand'
                      ? 'border-teal-600 bg-teal-50/50 dark:bg-teal-950/30 text-teal-700 dark:text-teal-300 font-bold'
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="bookingMode"
                    value="on_demand"
                    checked={bookingMode === 'on_demand'}
                    onChange={() => setBookingMode('on_demand')}
                    className="sr-only"
                  />
                  <Clock size={15} />
                  <span>⚡ On-Demand (Immediate)</span>
                </label>

                <label
                  className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                    bookingMode === 'scheduled'
                      ? 'border-teal-600 bg-teal-50/50 dark:bg-teal-950/30 text-teal-700 dark:text-teal-300 font-bold'
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="bookingMode"
                    value="scheduled"
                    checked={bookingMode === 'scheduled'}
                    onChange={() => setBookingMode('scheduled')}
                    className="sr-only"
                  />
                  <Calendar size={15} />
                  <span>📅 Schedule Later</span>
                </label>
              </div>
            </div>

            {bookingMode === 'scheduled' && (
              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Select Date & Time <span className="text-rose-500">*</span>
                </label>
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={e => setScheduledAt(e.target.value)}
                  required={bookingMode === 'scheduled'}
                  className="h-10"
                />
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Notes / Special instructions (optional)
              </label>
              <Textarea
                placeholder="Specific requirements, preferred timings or issues to fix..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                className="resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100 dark:border-slate-800">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setStep('catalog')}
                disabled={submitting}
                className="h-9 text-xs"
              >
                Back to Catalog
              </Button>
              <Button
                type="submit"
                disabled={submitting || locationStatus !== 'detected'}
                className="h-9 px-5 text-xs bg-teal-600 hover:bg-teal-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={13} className="animate-spin mr-1.5" />
                    Requesting Booking...
                  </>
                ) : locationStatus !== 'detected' ? (
                  'Turn on location to book'
                ) : (
                  'Confirm Booking'
                )}
              </Button>
            </div>
          </form>
        )}

        {/* ======================================================== */}
        {/* STEP 4: Success View (Booking Number, Status, Notes)     */}
        {/* ======================================================== */}
        {step === 'success' && successInfo && (
          <div className="overflow-y-auto p-6 sm:p-8 space-y-5 text-center flex-1">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto">
              <CheckCircle2 size={36} />
            </div>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Service Requested Successfully!
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Your booking request has been submitted to {selectedProvider?.business_name}.
              </p>
            </div>

            <div className="max-w-sm mx-auto bg-slate-50 dark:bg-slate-850 p-4 rounded-xl border border-slate-200 dark:border-slate-800 text-xs space-y-2 text-left">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Booking Number:</span>
                <span className="font-mono font-bold text-slate-900 dark:text-white">
                  {successInfo.bookingNumber}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Status:</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  {successInfo.status || 'Requested'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Service:</span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {selectedItem?.service_name}
                </span>
              </div>
            </div>

            {/* Requirement 4: booking_charge_note if booking_charge_due > 0 + "The business will contact you to confirm and collect it." No fake payment screen. */}
            {/* Task 3: Pay Now / Confirmation */}
            {successInfo.bookingChargeDue > 0 && (
              <div className="max-w-md mx-auto p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/20 border border-amber-300 dark:border-amber-800 rounded-2xl text-left space-y-3.5 shadow-sm">
                {isPaymentConfirmed ? (
                  <div className="flex items-center gap-2.5 p-3 rounded-xl bg-emerald-100/80 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 text-xs font-bold">
                    <CheckCircle2 size={20} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <div>
                      <p className="text-sm">Booking Confirmed!</p>
                      <p className="text-[11px] font-normal text-emerald-700 dark:text-emerald-400 mt-0.5">
                        Minimum booking advance payment received. Your professional will be assigned shortly.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                          Confirm Your Booking
                        </span>
                        <h4 className="text-base font-extrabold text-slate-900 dark:text-white mt-0.5">
                          Pay ₹{successInfo.bookingChargeDue.toFixed(2)} to confirm your booking
                        </h4>
                        <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5">
                          Adjusted in your final bill upon service completion.
                        </p>
                      </div>
                      <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 shrink-0">
                        <IndianRupee size={20} />
                      </div>
                    </div>

                    {paymentLinkUrl && (
                      <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-amber-200 dark:border-amber-900/40 space-y-3">
                        <div className="flex flex-col sm:flex-row items-center gap-3.5">
                          <div className="bg-white p-2 rounded-lg border border-slate-200 dark:border-slate-800 shadow-xs shrink-0">
                            <QRCodeSVG value={paymentLinkUrl} size={110} level="M" />
                          </div>
                          <div className="flex-1 min-w-0 space-y-1.5 text-center sm:text-left">
                            <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                              Scan QR or use UPI / Card / Netbanking
                            </div>
                            <div className="p-1.5 px-2 bg-slate-50 dark:bg-slate-800 rounded font-mono text-[11px] text-slate-700 dark:text-slate-300 truncate">
                              {paymentLinkUrl}
                            </div>
                            <div className="flex items-center justify-center sm:justify-start gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  navigator.clipboard.writeText(paymentLinkUrl);
                                  toast.success('Link copied to clipboard!');
                                }}
                                className="h-7 text-[11px] px-2.5 border-slate-200 dark:border-slate-700"
                              >
                                Copy link
                              </Button>
                              <a
                                href={paymentLinkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[11px] text-primary font-bold hover:underline inline-flex items-center gap-1"
                              >
                                <span>Pay Now</span>
                                <ArrowUpRight size={12} />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Button
                        type="button"
                        onClick={handlePayNow}
                        disabled={onlinePayLoading}
                        className="h-10 px-5 text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white gap-2 shadow-sm"
                      >
                        {onlinePayLoading ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <CreditCard size={14} />
                        )}
                        <span>{paymentLinkUrl ? 'Re-open Payment Page' : `Pay ₹${successInfo.bookingChargeDue.toFixed(2)} Now`}</span>
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        className="h-10 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                      >
                        Pay Later
                      </Button>
                    </div>

                    <p className="text-[10px] text-slate-500 dark:text-slate-400">
                      You can also pay later — the business can send this payment link directly to your WhatsApp.
                    </p>
                  </>
                )}
              </div>
            )}

            {successInfo.bookingChargeDue <= 0 && successInfo.bookingChargeNote && (
              <div className="max-w-sm mx-auto p-3.5 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-xl text-xs text-blue-900 dark:text-blue-200 text-left space-y-1">
                <p className="font-semibold">{successInfo.bookingChargeNote}</p>
                <p className="text-[11px] text-blue-700 dark:text-blue-300">
                  The business will contact you to confirm and collect it.
                </p>
              </div>
            )}

            <div className="pt-2">
              <Button
                type="button"
                onClick={onClose}
                className="bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-700 dark:hover:bg-slate-600 px-6 text-xs font-bold h-9"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Task 1: Login Sheet Gate */}
      {showLoginModal && (
        <ConsumerAuthModal
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => {
            setShowLoginModal(false);
            toast.success('Logged in successfully!');
            // Auto-proceed to booking form with selections kept
            if (selectedItem) {
              setStep('book');
            }
          }}
        />
      )}
    </div>
  );
}
